import type { SkyPhase } from '@iqrapath/shared'
import { useEffect, useRef, type CSSProperties } from 'react'

/**
 * "Setapak": the floating garden island (docs/ASSET-BRIEF.md §3), generated art in public/img/isle/<phase>-<on|off>.webp.
 * All 8 images share one crop, so the lamp-on layer cross-fades exactly over the lamp-off layer.
 *
 * Motion (docs/UX-PLAYBOOK.md §8): slow float, parallax on pointer / device tilt (rotateX ≤ 5°, rotateY ≤ 7°), breathing
 * lamp glow and fireflies while the lamps are on. Everything stops with prefers-reduced-motion.
 */

type Props = { phase: SkyPhase; lit: boolean; className?: string }

/** Lamp glass centres in % of the image (measured on the master, tools/assets/out/lamp-crops.png). */
export const LAMPS = [
  { x: 12, y: 37.9 },
  { x: 45.7, y: 33.9 },
  { x: 71.8, y: 24.7, main: true },
] as const
export const MAIN_LAMP = LAMPS[2]

/** Fireflies over the garden: deterministic spots, drift and delay (no layout shift, no randomness between renders). */
const FIREFLIES = Array.from({ length: 10 }, (_, i) => ({
  x: 14 + ((i * 37) % 72),
  y: 22 + ((i * 23) % 38),
  dur: 6 + (i % 4) * 1.3,
  delay: -((i * 1.7) % 6),
  dx: ((i % 3) - 1) * 14,
}))

const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Parallax: pointer on desktop, device tilt on phones that expose it without a permission prompt. */
function useTilt() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || reducedMotion()) return
    let raf = 0
    let x = 0
    let y = 0
    const clamp = (v: number) => Math.max(-1, Math.min(1, v))
    const apply = () => {
      raf = 0
      el.style.setProperty('--rx', `${(-y * 5).toFixed(2)}deg`)
      el.style.setProperty('--ry', `${(x * 7).toFixed(2)}deg`)
      el.style.setProperty('--fx', `${(x * 10).toFixed(1)}px`)
    }
    const set = (nx: number, ny: number) => { x = clamp(nx); y = clamp(ny); if (!raf) raf = requestAnimationFrame(apply) }
    const onPointer = (e: PointerEvent) => { if (e.pointerType === 'mouse') set((e.clientX / window.innerWidth - 0.5) * 2, (e.clientY / window.innerHeight - 0.5) * 2) }
    const onTilt = (e: DeviceOrientationEvent) => { if (e.gamma != null && e.beta != null) set(e.gamma / 25, (e.beta - 45) / 25) }
    window.addEventListener('pointermove', onPointer, { passive: true })
    window.addEventListener('deviceorientation', onTilt, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onPointer)
      window.removeEventListener('deviceorientation', onTilt)
      cancelAnimationFrame(raf)
    }
  }, [])
  return ref
}

export function Island({ phase, lit, className = '' }: Props) {
  const tilt = useTilt()
  const src = (lamp: 'on' | 'off') => ({
    src: `/img/isle/${phase}-${lamp}.webp`,
    srcSet: `/img/isle/${phase}-${lamp}.webp 640w, /img/isle/${phase}-${lamp}@2x.webp 1280w`,
    sizes: '(min-width: 460px) 400px, 88vw',
  })
  return (
    <div
      className={`island isle ${lit ? 'is-lit' : 'is-dark'} ${className}`}
      data-phase={phase}
      role="img"
      aria-label={lit ? 'Pulau taman dengan lampu yang menyala' : 'Pulau taman dengan lampu yang padam'}
    >
      <div className="isle-tilt" ref={tilt}>
        <div className="isle-bob">
          <img className="isle-layer" {...src('off')} alt="" width={640} height={640} decoding="async" draggable={false} />
          <img className="isle-layer isle-on" {...src('on')} alt="" width={640} height={640} decoding="async" draggable={false} />
          {lit && (
            <div className="isle-fx" aria-hidden="true">
              {LAMPS.map((l) => (
                <i key={l.x} className={`lamp-glow${'main' in l ? ' main' : ''}`} style={{ left: `${l.x}%`, top: `${l.y}%` }} />
              ))}
              {phase !== 'siang' && FIREFLIES.map((f, i) => (
                <i key={i} className="firefly" style={{ left: `${f.x}%`, top: `${f.y}%`, animationDuration: `${f.dur}s`, animationDelay: `${f.delay}s`, '--dx': `${f.dx}px` } as CSSProperties} />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="isle-shadow" aria-hidden="true" />
    </div>
  )
}
